import type { SaveData } from "../types";

// localStorage-backed persistence. Structured behind a tiny async-friendly API
// so it can be swapped for @capacitor/preferences on native without touching
// callers (Preferences is async; we keep the read path sync via an in-memory
// cache hydrated at boot).

const STORAGE_KEY = "immune-defender.save.v1";
const CURRENT_VERSION = 1;

const DEFAULTS: SaveData = {
  version: CURRENT_VERSION,
  points: 0,
  hasSeenDisclaimer: false,
  lastCheckIn: null,
};

let cache: SaveData = { ...DEFAULTS };

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch {
    // Storage may be unavailable (private mode / WebView quirks). Fail soft —
    // the game stays playable for the current session.
  }
}

export const SaveState = {
  /** Hydrate the in-memory cache from storage. Call once at boot. */
  load(): SaveData {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<SaveData>;
        cache = { ...DEFAULTS, ...parsed, version: CURRENT_VERSION };
      }
    } catch {
      cache = { ...DEFAULTS };
    }
    return cache;
  },

  get(): Readonly<SaveData> {
    return cache;
  },

  get points(): number {
    return cache.points;
  },

  addPoints(amount: number): number {
    cache.points = Math.max(0, Math.floor(cache.points + amount));
    persist();
    return cache.points;
  },

  /** Returns true the first time it's called per install (then records it). */
  consumeDisclaimer(): boolean {
    const needed = !cache.hasSeenDisclaimer;
    if (needed) {
      cache.hasSeenDisclaimer = true;
      persist();
    }
    return needed;
  },

  recordCheckIn(isoDate: string): void {
    cache.lastCheckIn = isoDate;
    persist();
  },
};
